import { Session } from "openai/resources/beta/realtime/sessions"
import { initializeRTC } from '../AICoach/RTCConnection';
import React, { PropsWithChildren, PropsWithRef, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react"
import { get_deployment, usePost } from "../API";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { ErrorEvent, RealtimeClientEvent, RealtimeServerEvent } from "openai/resources/beta/realtime/realtime";
import { safeParse, useLogger } from "../Util";
import * as Sentry from "@sentry/react";
import { useEventEmitter } from "../Hooks/EventListener";
import { AICoachState, useAICoach } from "../AICoach/AICoachContext";

//TODO(luke): remove the OpenAI parts from this potentially, depending on compatibility of other WebRTC services.
export type WebRTCContextType = {
  channel: RTCDataChannel | null,
  peerConnection: RTCPeerConnection | null,
  sender: RTCRtpSender | null,
  channelState: { status: 'closed' | 'open' | 'initializing' } | { status: 'error', reason: string },
  /**
   * Indicates that the WebRTC feature has been enabled.
   * This means that a connection has been initiated, but the `active` flag 
   * is what actually determines readiness to receive messages.
   */
  enabled: boolean,
  /**
   * Indicates that a connection is open and ready to accept input
   */
  active: boolean,
  setEnabled(enable: boolean): void,
  realtimeSession: Session | null,
  /**
   * indicates what the chat assistant is currently doing,
   * based on events flowing in.
   */
  phase: 'unknown' | 'listening' | 'speaking' | 'thinking',
  outputStream: MediaStream | null,
  reconnectCount: number,
  /**
   * subscribe to the events of the realtime chat,
   * specifically to represent conversational state.
   * Text Inputs & Transcriptions will come through here,
   * among other events.
   */
  subscribe(
    /**
     * The callback that is used whenever a new event is observed.
     */
    fn: (event: (RealtimeServerEvent | RealtimeClientEvent)) => void, 
    /**
     * Sends all previously observed events to the subscriber,
     * so that it may get up-to-date prior to accepting new events.
     */
    initial?: (existing: (RealtimeServerEvent | RealtimeClientEvent)[]) => void
  ): () => void,
  /**
   * Changes the session's behavior, such as updating the modality from text to audio,
   * or changing the language transcribed.
   *
   * NOTE: certain updates are not effective after initial communication,
   * i.e. changing the voice will not work after using audio for the first time.
   */
  updateSession(update: Session): void,
  /**
   * Sends an event to the WebRTC client event channel.
   */
  sendEvent(message: RealtimeClientEvent): void,
  /**
   * Performs a reconnection, clearing out any existing state.
   */
  reset(): void,
  errors?: Extract<RealtimeServerEvent, { type: 'error' }>[]
}

export const WebRTCContext = React.createContext<WebRTCContextType>({
  channel: null,
  peerConnection: null,
  sender: null,
  enabled: false,
  active: false,
  realtimeSession: null,
  outputStream: null,
  phase: 'unknown',
  channelState: { status: 'closed' },
  reconnectCount: 0,
  updateSession() { console.warn('no WebRTCContext provided, ignoring updateSession'); },
  setEnabled() { console.warn('no WebRTCContext provided, ignoring setEnabled'); },
  sendEvent() { console.warn('no WebRTCContext provided, ignoring sendEvent'); },
  reset() { console.warn('no WebRTCContext provided, ignoring reset'); },
  subscribe() { console.warn('no WebRTCContext provided, ignoring subscribe'); return () => { } }
});

export const WebRTCProvider: React.FC<{ context: WebRTCContextType, children: React.ReactNode }> = ({ context, children }) => {
  return <WebRTCContext.Provider value={context}>
    {children}
  </WebRTCContext.Provider>
};

export function OpenAIWebRTCProvider({ children, modality }: PropsWithChildren & Pick<AICoachState, "modality">) {
  const logger = useLogger(OpenAIWebRTCProvider);
  const [channel, setChannel] = useState<RTCDataChannel | null>(null);
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const [sender, setSender] = useState<RTCRtpSender | null>(null);
  const [realtimeSession, setRealtimeSession] = useState<Session | null>(null);
  const [outputStream, setOutputStream] = useState<MediaStream | null>(null);
  const [channelState, setChannelState] = useState<WebRTCContextType['channelState']>({ status: 'closed' });
  const [enabled, setEnabled] = useState(modality === "audio");
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<'unknown' | 'listening' | 'speaking' | 'thinking'>('unknown');
  const reconnectCount = useRef<number>(0);
  const [errors, setErrors] = useState<ErrorEvent[]>([]);
  const { emit, subscribe } = useEventEmitter<(RealtimeServerEvent | RealtimeClientEvent)>();

  const createToken = usePost('/coach/token');

  /**
  * Connect to OpenAI Realtime via WebRTC
  * @param cancel A ControlSignal which can be used to cancel the connection (optional)
  */
  const connect = useCallback(async (cancel?: AbortSignal) => {
    logger.info("connecting to openai");

    if (reconnectCount.current > 5) {
      console.warn("Exceeded reconnect attempts");
      return;
    }
    reconnectCount.current += 1;

    if (cancel?.aborted) {
      logger.info('cancelled connecting!');
      return;
    }

    const { token } = await createToken({ modality: 'text' });

    if (!token) {
      logger.info('invalid token', token);
      return;
    }

    setChannelState({ status: 'initializing' });

    const { dataChannel, pc, sender } = await initializeRTC(token, {
      onReady() {
        logger.info('started connection successfully');
        reconnectCount.current = 0;
        setChannel(dataChannel);
        setSender(sender);
        setPeerConnection(pc);
        setChannelState({ status: 'open' });
        setActive(true);
      },
      onTrack(e) {
        if (e.streams[0]) {
          setOutputStream(e.streams[0]);
        }
      },
      onConnectionError(e) {
        setActive(false);
        setChannelState({ status: 'error', reason: e });
        setSender(null)
        setPeerConnection(null);
        setChannel(null);
        setErrors(errors.concat({
          type: 'error',
          event_id: '',
          error: {
            type: 'rtc_connection_error',
            code: 'unable_to_connect',
            message: 'Unable to connect to WebRTC endpoint',
            event_id: e
          }
        }));
      },
      onClose() {
        setActive(false);
        setChannelState({ status: 'closed' });
        setSender(null);
        setPeerConnection(null);
        setChannel(null);
      },
      onError() {
        setActive(false);
        setChannelState({ status: 'error', reason: 'unknown' });
        setSender(null);
        setPeerConnection(null);
        setChannel(null);
        setErrors(errors.concat({
          type: 'error',
          event_id: '',
          error: {
            type: 'rtc_connection_error',
            code: 'unable_to_connect',
            message: 'Unable to connect to WebRTC endpoint'
          }
        }));
      }
    });

    cancel?.addEventListener('abort', () => {
      disconnect(dataChannel);
    }, { once: true, passive: true });
  }, [errors]);
  
  const disconnect = (channel: RTCDataChannel | null) => { 
    channel?.close();
    setChannelState({ status: 'closed' });
  }

  useAsyncEffect(async () => {
    if (enabled && !active && modality === "audio") {
      await connect();
    }
    else if (modality === "text") {
      disconnect(channel)
    }
  }, [enabled, active, modality]);

  function phaseAdapter(type: (RealtimeServerEvent | RealtimeClientEvent)['type']): typeof phase {
    switch (type) {
      case "response.audio.delta":
      case "output_audio_buffer.started":
      case "response.content_part.added":
      case "response.text.delta":
      case "response.audio_transcript.delta":
        return 'speaking';
      case "response.output_item.added":
      case "response.created":
        return 'thinking';
      case "input_audio_buffer.speech_started":
      default:
        return 'listening';
    }
  };

  useEffect(() => {
    const messageHandler = (msg: MessageEvent) => {
      const event = safeParse(msg.data) as RealtimeServerEvent | null;
      if (event) {
        setPhase(phaseAdapter(event.type));
        switch (event.type) {
          case "error":
            setErrors(errors.concat(event));
            break;
          case "session.created":
          case "session.updated":
            setRealtimeSession(event.session);
            break;
          default:
            emit(event);
            break;
        }
      }
    };

    if (channel) {
      channel.addEventListener('message', messageHandler);
    }

    return () => channel?.removeEventListener('message', messageHandler);
  }, [emit, channel]);

  const sendEvent = useCallback((message: RealtimeClientEvent) => {
    if (channel && channelState.status === "open" && channel.readyState === "open") {
      setPhase(phaseAdapter(message.type));
      channel?.send(JSON.stringify(message));
      emit(message);
    }
  }, [emit, channel, channel?.readyState, channelState]);

  const update = useCallback((session: Partial<Session>) => {
    sendEvent({
      type: 'session.update',
      session
    });
  }, [sendEvent]);

  useEffect(() => {
    if (errors?.length) {
      setActive(false);
      setChannelState({ status: 'error', reason: errors?.map(e => e.error.message).join('\n') ?? 'unknown' });
      setSender(null);
      setPeerConnection(null);
      setChannel(null);

      logger.error(errors);

      const nonExpiredErrors = errors?.filter(e => e.error.code !== "session_expired");

      // all non session_expired events should be logged
      if (nonExpiredErrors?.length) {
        const deploymentName = get_deployment();
        Sentry.setContext("deployment", { "name": deploymentName });
        Sentry.setTag("deployment_name", deploymentName);
        Sentry.captureException(new Error('AI Coach Errors Encountered'), { extra: { errors } });
      }
    }
  }, [errors]);

  const reset = useCallback(() => {
    const orig = enabled;
    setEnabled(false);
    setTimeout(() => setEnabled(orig), 100);
  }, []);
  
  useEffect(() => {
    setEnabled(modality === "audio");
  }, [modality]);

  return <WebRTCProvider context={{
    channel,
    reset,
    active,
    peerConnection,
    channelState,
    sendEvent,
    updateSession: update,
    reconnectCount: reconnectCount.current,
    phase,
    setEnabled,
    enabled,
    realtimeSession,
    sender,
    outputStream,
    subscribe
  }}>
    {children}
  </WebRTCProvider>
}
